import Section from '@app/components/atoms/Section/Section'
import ImageAvatar from '@app/components/ImageAvatar'
import ModalUpdateStatus from '@app/components/ModalUpdateStatus'
import TwoFactorAuthenticationDialog from '@app/components/TwoFactorAuthenticationDialog'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { usePage } from '@inertiajs/inertia-react'
import DeleteIcon from '@mui/icons-material/Delete'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import { Link } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import axios from 'axios'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Translation, useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { PasswordForm, ProfileForm } from './ProfileSchema'

const Profile = () => {
  const OTPForm = Yup.object().shape({
    otp: Yup.string()
      .trim()
      .required(<Translation>{(t) => t('VALIDATE.OTP')}</Translation>),
  })

  const [qrcode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [openModal, setOpenModal] = useState(false)
  const [openModalRemove2FA, setOpenModalRemove2FA] = useState(false)
  const [showModalQR, setShowModalQR] = useState(false)
  const { t } = useTranslation()

  const handleClose = () => {
    setShowModalQR(false)
    setOpenModal(false)
    resetOTP()
  }
  const {
    props: { user },
  } = usePage()

  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: user.name,
      email: user.email,
      phone: user.phone,
    },
    resolver: yupResolver(ProfileForm),
  })

  const {
    register: registerOTP,
    handleSubmit: handleSubmitOTP,
    reset: resetOTP,
    formState: { errors: errorsOTP },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      otp: '',
    },
    resolver: yupResolver(OTPForm),
  })

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset,
    trigger: triggerPassword,
    setError: setErrorsPassword,
    formState: { errors: errorsPassword },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    resolver: yupResolver(PasswordForm),
  })

  const onSubmitInfo = (data) => {
    Inertia.put(route('partner.profile.update', user.id), data, {
      preserveScroll: (page) => Object.keys(page.props.errors).length,
    })
  }

  const onSubmitPassword = (data) => {
    Inertia.post(route('password.change'), data, {
      onSuccess: () => {
        reset()
      },
      onError: (error) => {
        setErrorsPassword('currentPassword', error)
      },
      preserveScroll: (page) => Object.keys(page.props.errors).length,
    })
  }

  const onSubmitOTP = async (data) => {
    Inertia.post(route('complete.registration'), data, {
      onSuccess: () => {
        handleClose()
        resetOTP()
      },
    })
  }

  const onGetQR = async () => {
    await axios.get('/qr', { params: { email: user.email } }).then((response) => {
      setQrCode(response.data.qr_code)
      setSecret(response.data.secret)
    })
    setOpenModal(true)
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Section>
            <ImageAvatar />
            <Grid container noValidate component="form" onSubmit={handleSubmit(onSubmitInfo)}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  label={t('PROFILE.NAME')}
                  margin="normal"
                  name="name"
                  size="small"
                  type="text"
                  {...register('name')}
                  variant="outlined"
                  onFocus={() => trigger('name')}
                />
                <TextField
                  disabled
                  fullWidth
                  required
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  label={t('PROFILE.EMAIL')}
                  margin="normal"
                  name="email"
                  size="small"
                  type="email"
                  {...register('email')}
                  variant="outlined"
                  onFocus={() => trigger('email')}
                />
                <TextField
                  fullWidth
                  required
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  label={t('PROFILE.PHONE')}
                  margin="normal"
                  name="phone"
                  size="small"
                  type="tel"
                  {...register('phone')}
                  variant="outlined"
                  onFocus={() => trigger('phone')}
                />
                <Button color="primary" type="submit" variant="contained">
                  {t('PROFILE.UPDATE')}
                </Button>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={6}>
          <Section>
            <Typography variant="h5">{t('PROFILE.CHANGE_PASSWORD')}</Typography>
            <Grid container noValidate component="form" onSubmit={handleSubmitPassword(onSubmitPassword)}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  error={!!errorsPassword.currentPassword}
                  helperText={errorsPassword.currentPassword?.message}
                  label={t('PROFILE.CURRENT_PASSWORD')}
                  margin="normal"
                  name="currentPassword"
                  size="small"
                  type="password"
                  {...registerPassword('currentPassword')}
                  variant="outlined"
                  onFocus={() => triggerPassword('currentPassword')}
                />
                <TextField
                  fullWidth
                  required
                  error={!!errorsPassword.newPassword}
                  helperText={errorsPassword.newPassword?.message}
                  label={t('PROFILE.NEW_PASSWORD')}
                  margin="normal"
                  name="newPassword"
                  size="small"
                  type="password"
                  {...registerPassword('newPassword')}
                  variant="outlined"
                  onFocus={() => triggerPassword('newPassword')}
                />
                <TextField
                  fullWidth
                  required
                  error={!!errorsPassword.confirmNewPassword}
                  helperText={errorsPassword.confirmNewPassword?.message}
                  label={t('PROFILE.CONFIRM_NEW_PASSWORD')}
                  margin="normal"
                  name="confirmNewPassword"
                  size="small"
                  type="password"
                  {...registerPassword('confirmNewPassword')}
                  variant="outlined"
                  onFocus={() => triggerPassword('confirmNewPassword')}
                />
                <Button color="primary" type="submit" variant="contained">
                  {t('PROFILE.CHANGE_PASSWORD')}
                </Button>
              </Grid>
            </Grid>
          </Section>

          <Section margin={true}>
            <Typography variant="h5">{t('PROFILE.SECURITY')}</Typography>
            {!user.google2fa_secret ? (
              <Link
                component="button"
                sx={{
                  paddingY: '1.5rem',
                  fontSize: '1rem',
                  textDecoration: 'none',
                  outline: 'none !important',
                }}
                variant="body2"
                onClick={onGetQR}
              >
                {t('PROFILE.2FA')}
              </Link>
            ) : (
              <Grid container spacing={2} style={{ alignItems: 'center', padding: '20px 0' }}>
                <Grid item xs={1}>
                  <QrCodeScannerIcon fontSize="medium"></QrCodeScannerIcon>
                </Grid>
                <Grid item xs={8}>
                  <Typography gutterBottom>Authenticator</Typography>
                </Grid>
                <Grid item style={{ textAlign: 'right' }} xs={3}>
                  <DeleteIcon
                    fontSize="medium"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpenModalRemove2FA(true)}
                  ></DeleteIcon>
                </Grid>
              </Grid>
            )}
          </Section>
        </Grid>
      </Grid>
      <ModalUpdateStatus
        content={t('MODAL.REMOVE_2FA_CONTENT')}
        handleCloseModal={() => setOpenModalRemove2FA(false)}
        handleSubmit={() => {
          Inertia.delete(route('profile.remove2FA'), {
            onSuccess: () => {
              setOpenModalRemove2FA(false)
            },
          })
        }}
        openModalConfirm={openModalRemove2FA}
        title={t('MODAL.REMOVE_2FA')}
      />
      <TwoFactorAuthenticationDialog
        errorsOTP={errorsOTP}
        handleClose={handleClose}
        handleSubmitOTP={handleSubmitOTP}
        openModal={openModal}
        qrcode={qrcode}
        registerOTP={registerOTP}
        secret={secret}
        setShowModalQR={setShowModalQR}
        showModalQR={showModalQR}
        onSubmitOTP={onSubmitOTP}
      ></TwoFactorAuthenticationDialog>
    </>
  )
}

export default Profile
